import { GlassCard, ScrollReveal } from '@/components/design-system';
import { EmptyState } from '@/components/poof-ui';
import { Button } from '@/components/ui/button';
import { useRealtimeData } from '@/hooks/use-realtime-data';
import { Address } from '@/lib/db-client';
import { subscribeManyListings, updateListings, type ListingsResponse } from '@/lib/collections/listings';
import { cn } from '@/lib/utils';
import { CITIES, PLASTIC_TYPES, PLASTIC_TYPE_LABELS, formatCentsAsUsd, isPlasticType, type City, type PlasticType } from '@/utils/plastic-rates';
import { CheckCircle2, MapPin, Package, PackageSearch } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

interface BrowseListingsPanelProps {
  userAddress: string;
}

/** Live grid of open recovery listings, filterable by plastic type + city, with an Accept action per card. */
export function BrowseListingsPanel({ userAddress }: BrowseListingsPanelProps) {
  const { data: listings, loading } = useRealtimeData<ListingsResponse[]>(subscribeManyListings, true, '');
  const [plasticFilter, setPlasticFilter] = useState<'all' | PlasticType>('all');
  const [cityFilter, setCityFilter] = useState<'all' | City>('all');
  const [acceptingId, setAcceptingId] = useState<string | null>(null);

  const open = (listings ?? []).filter(
    (l) =>
      l.status === 'open' &&
      (plasticFilter === 'all' || l.plasticType === plasticFilter) &&
      (cityFilter === 'all' || l.city === cityFilter)
  );

  const handleAccept = async (listing: ListingsResponse) => {
    setAcceptingId(listing.id);
    try {
      const success = await updateListings(listing.id, {
        acceptedBy: Address.publicKey(userAddress),
        status: 'accepted',
      });
      if (success) {
        toast.success('Accepted — schedule the pickup in My Pickups.');
      } else {
        toast.error('Could not accept this listing. It may already be taken.');
      }
    } catch (err) {
      console.error('Accept listing failed', err);
      toast.error('Something went wrong accepting this listing.');
    } finally {
      setAcceptingId(null);
    }
  };

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-center gap-2">
        <FilterPill active={plasticFilter === 'all'} onClick={() => setPlasticFilter('all')} label="All types" />
        {PLASTIC_TYPES.map((type) => (
          <FilterPill
            key={type}
            active={plasticFilter === type}
            onClick={() => setPlasticFilter(type)}
            label={PLASTIC_TYPE_LABELS[type]}
          />
        ))}
        <span className="mx-1 h-4 w-px bg-border/60" />
        <FilterPill active={cityFilter === 'all'} onClick={() => setCityFilter('all')} label="All cities" />
        {CITIES.map((city) => (
          <FilterPill key={city} active={cityFilter === city} onClick={() => setCityFilter(city)} label={city} />
        ))}
      </div>

      {!loading && open.length === 0 && (
        <EmptyState
          icon={<PackageSearch />}
          title="No open listings"
          description="Nothing matches these filters right now — check back soon."
        />
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {open.map((listing, i) => (
          <ScrollReveal key={listing.id} delay={Math.min(i * 0.05, 0.3)}>
            <GlassCard padding="sm" className="flex h-full flex-col">
              <div className="h-32 w-full overflow-hidden rounded-xl bg-secondary/50">
                {listing.photoUrl ? (
                  <img src={listing.photoUrl} alt="" className="h-full w-full object-cover" />
                ) : (
                  <div className="flex h-full w-full items-center justify-center">
                    <Package className="h-6 w-6 text-muted-foreground" />
                  </div>
                )}
              </div>

              <div className="mt-3 flex flex-1 flex-col">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-semibold text-foreground">
                    {isPlasticType(listing.plasticType) ? PLASTIC_TYPE_LABELS[listing.plasticType] : listing.plasticType}
                  </span>
                  <span className="text-sm font-medium text-foreground">{listing.estimatedKg} kg</span>
                </div>

                <p className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
                  <MapPin className="h-3 w-3" />
                  {listing.locationName}, {listing.city}
                </p>

                <div className="mt-3 rounded-xl bg-gradient-to-br from-[hsl(var(--amber))]/15 to-[hsl(var(--amber))]/5 px-3 py-2 ring-1 ring-[hsl(var(--amber))]/30">
                  <span className="text-[10px] uppercase tracking-wide text-[hsl(var(--amber))]">Recovery worth</span>
                  <p className="font-heading text-lg font-bold text-[hsl(var(--amber))]">
                    {formatCentsAsUsd(listing.estimatedValue)}
                  </p>
                </div>

                <Button
                  onClick={() => handleAccept(listing)}
                  disabled={acceptingId === listing.id}
                  className="mt-3 w-full bg-gradient-to-r from-primary to-accent text-primary-foreground hover:opacity-90"
                >
                  <CheckCircle2 className="mr-2 h-4 w-4" />
                  {acceptingId === listing.id ? 'Accepting...' : 'Accept'}
                </Button>
              </div>
            </GlassCard>
          </ScrollReveal>
        ))}
      </div>
    </div>
  );
}

function FilterPill({ active, onClick, label }: { active: boolean; onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded-full border px-3 py-1.5 text-xs font-medium transition-all',
        active
          ? 'border-primary/60 bg-gradient-to-r from-primary/15 to-accent/15 text-foreground'
          : 'border-border/60 bg-secondary/30 text-muted-foreground hover:border-border'
      )}
    >
      {label}
    </button>
  );
}

export default BrowseListingsPanel;
